import { EnvelopeSummary } from '@signbento/contracts'
import type { ApiTransport } from './api-client.js'
import { loadRequestPage, requestTimestamp } from './agreement-views.js'
import type { LocalCompletedItem } from './local-home.js'

/**
 * Home's Waiting on others and Completed compartments (PRD §3.1 Recent, split per the approved
 * Home deviation). Each shows at most three rows and never a count.
 */
export const HOME_COMPARTMENT_LIMIT = 3

export type HomeRequestsOutcome =
  | {
      readonly kind: 'READY'
      readonly waiting: readonly EnvelopeSummary[]
      readonly completed: readonly EnvelopeSummary[]
    }
  | { readonly kind: 'SIGNED_OUT' }
  | { readonly kind: 'FAILED'; readonly code: string }

/**
 * One read of the sender's newest active and completed requests. It over-reads so a request
 * already listed under Needs attention can be left out of Waiting without leaving the box short.
 */
export async function loadHomeRequests(transport: ApiTransport): Promise<HomeRequestsOutcome> {
  const outcome = await loadRequestPage({
    transport,
    states: ['ACTIVE', 'COMPLETED'],
    cursors: {},
    limit: HOME_COMPARTMENT_LIMIT * 2,
  })
  if (outcome.kind !== 'READY') return outcome
  return {
    kind: 'READY',
    waiting: outcome.items.filter((item) => item.state === 'ACTIVE'),
    completed: outcome.items.filter((item) => item.state === 'COMPLETED'),
  }
}

/** Requests out for signature, minus any Needs attention already shows. */
export function waitingRows(
  waiting: readonly EnvelopeSummary[],
  attentionEnvelopeIds: ReadonlySet<string>,
): readonly EnvelopeSummary[] {
  return waiting
    .filter((item) => !attentionEnvelopeIds.has(item.envelope_id))
    .slice(0, HOME_COMPARTMENT_LIMIT)
}

export type CompletedRow =
  | { readonly kind: 'local'; readonly at: string; readonly item: LocalCompletedItem }
  | { readonly kind: 'remote'; readonly at: string; readonly item: EnvelopeSummary }

/**
 * Local completions and completed requests, newest first. A request whose document this device
 * also completed is listed once, as the local row, because that row can offer Use again.
 */
export function completedRows(
  local: readonly LocalCompletedItem[],
  remote: readonly EnvelopeSummary[],
): readonly CompletedRow[] {
  const localEnvelopes = new Set(
    local
      .map(({ document }) => document.envelope_id)
      .filter((id): id is string => id !== undefined),
  )
  const rows: CompletedRow[] = [
    ...local.map((item) => ({
      kind: 'local' as const,
      at: item.completion.completed_at,
      item,
    })),
    ...remote
      .filter((item) => !localEnvelopes.has(item.envelope_id))
      .map((item) => ({ kind: 'remote' as const, at: requestTimestamp(item), item })),
  ]
  return rows
    .sort((a, b) => (Date.parse(b.at) || 0) - (Date.parse(a.at) || 0))
    .slice(0, HOME_COMPARTMENT_LIMIT)
}

/** The vault setting that keeps Home's last good read, so a failed refresh still shows rows. */
export const HOME_REQUESTS_CACHE_KEY = 'home.requests_cache' as const

export interface HomeRequestsSnapshot {
  /** The account the rows were read for: another account on this vault never sees them. */
  readonly account_id: string
  readonly at: string
  readonly waiting: readonly EnvelopeSummary[]
  readonly completed: readonly EnvelopeSummary[]
}

/**
 * Reads the last good snapshot back, defensively: it is a convenience on this device, so anything
 * that doesn't look like one, or was read for a different account, is ignored rather than shown
 * (PRD MET-10: cached remote data is marked stale with its as-of time; LOC-08: account data never
 * crosses an identity change).
 */
export function parseHomeSnapshot(value: unknown, accountId: string): HomeRequestsSnapshot | null {
  if (typeof value !== 'object' || value === null) return null
  const candidate = value as Partial<HomeRequestsSnapshot>
  if (
    candidate.account_id !== accountId ||
    typeof candidate.at !== 'string' ||
    Number.isNaN(Date.parse(candidate.at)) ||
    !Array.isArray(candidate.waiting) ||
    !Array.isArray(candidate.completed)
  )
    return null
  const valid = (items: readonly unknown[]): EnvelopeSummary[] =>
    items.flatMap((item) => {
      const parsed = EnvelopeSummary.safeParse(item)
      return parsed.success ? [parsed.data] : []
    })
  return {
    account_id: accountId,
    at: candidate.at,
    waiting: valid(candidate.waiting),
    completed: valid(candidate.completed),
  }
}
